import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlus } from '@fortawesome/free-solid-svg-icons';
import ColumnVisibilityModal from '../../components/ColumnVisibilityModal';
import { Link } from 'react-router-dom';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import ProdutoFilterBar from '../../components/ProdutoFilterBar';
import produtoService from '../../services/produtoService';
import { withPermissionAndLevel } from '../../components/withPermission';

const columns = [
  { Header: 'ID', accessor: 'idProduto' },
  { Header: 'Nome', accessor: 'nome' },
  { Header: 'Status', accessor: 'status' },
  { Header: 'Valor', accessor: 'valor', Cell: value => value != null ? Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '' },
  { Header: 'Tipo Produto', accessor: 'tipoProdutoNome' },
  { Header: 'Conta', accessor: 'contaNome' },
  { Header: 'Cadastrado Por', accessor: 'cadastradoPor' },
  { Header: 'Alterado Por', accessor: 'alteradoPor' },
  { Header: 'Cadastrado Em', accessor: 'cadastradoEm', Cell: value => {
    if (!value) return '';
    const date = new Date(value);
    const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return adjustedDate.toLocaleDateString('pt-BR');
  }},
  { Header: 'Alterado Em', accessor: 'alteradoEm', Cell: value => {
    if (!value) return '';
    const date = new Date(value);
    const adjustedDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    return adjustedDate.toLocaleDateString('pt-BR');
  }},
];

const pageSizes = [10, 20, 50, 100];

const ProdutoList = () => {
  const [produtos, setProdutos] = useState([]);
  const [filters, setFilters] = useState({ nome: '', status: '', tipoProduto: '' });
  const [debouncedFilters, setDebouncedFilters] = useState({ nome: '', status: '', tipoProduto: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[0]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(
    columns
      .filter(col => !['cadastradoPor', 'alteradoPor', 'cadastradoEm', 'alteradoEm'].includes(col.accessor))
      .map(col => col.accessor)
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Ref para armazenar o timer do debounce
  const debounceTimer = useRef(null);

  // Debounce dos filtros (600ms de delay)
  useEffect(() => {
    // Limpar timer anterior
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Criar novo timer
    debounceTimer.current = setTimeout(() => {
      setDebouncedFilters(filters);
      setPage(1); // Reset para primeira página ao filtrar
    }, 600);

    // Cleanup
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [filters]);

  const loadProdutos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        limit: pageSize,
        search: debouncedFilters.nome,
        status: debouncedFilters.status,
        tipoProduto: debouncedFilters.tipoProduto
      };

      const response = await produtoService.list(params);
      setProdutos(response.data.produtos);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
      setError('Erro ao carregar produtos');
      setProdutos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProdutos();
  }, [page, pageSize, debouncedFilters]);

  // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  const handleFilterChange = (e) => {
    const newFilters = { ...filters, [e.target.name]: e.target.value };
    setFilters(newFilters);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(1); // Reset para primeira página ao mudar tamanho
  };

  if (loading) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg text-gray-600">Carregando produtos...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg text-red-600">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Listagem de Produtos</h2>
            <Link
              to="/produtos/create"
              className="px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Novo Produto</span>
            </Link>
          </div>
          <ColumnVisibilityModal
            columns={columns}
            visibleColumns={visibleColumns}
            onChange={handleToggleColumn}
            onClose={handleCloseModal}
            isOpen={isModalOpen}
          />
          <div className="max-w-full">
            <ProdutoFilterBar filters={filters} onChange={handleFilterChange} />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
            <div className="order-2 sm:order-1 w-full sm:w-auto">
              <Pagination 
                currentPage={pagination.page} 
                totalPages={pagination.totalPages}
                totalItems={pagination.total || 0}
                itemsPerPage={pageSize}
                onPageChange={handlePageChange}
              />
            </div>
            <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
              <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={e => handlePageSizeChange(Number(e.target.value))}
                className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                {pageSizes.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <button
                type="button"
                className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
                onClick={handleOpenModal}
                aria-label="Exibir/Ocultar colunas"
              >
                <FontAwesomeIcon icon={faEye} />
                <span>Colunas</span>
              </button>
            </div>
          </div>
          <div className="max-w-full">
            <Table
              columns={columns.filter(col => visibleColumns.includes(col.accessor)).map(col => {
                if (col.accessor === 'nome') {
                  return {
                    ...col,
                    Cell: (value, row) => (
                      <div className="flex items-center gap-2">
                        <span>{value}</span>
                        <Link
                          to={`/produtos/${row.idProduto}/edit`}
                          className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none"
                          title="Editar"
                        >
                          <FontAwesomeIcon icon={faPen} />
                        </Link>
                      </div>
                    )
                  };
                }
                return col;
              })}
              data={produtos}
            />
          </div>
          {pagination.total > 0 && (
            <div className="mt-4 text-sm text-gray-600 text-center">
              Mostrando {produtos.length} de {pagination.total} produtos
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default withPermissionAndLevel(
  ProdutoList, 
  'produtos.list', 
  3, 
  'Você não tem permissão para acessar o módulo de Produtos.'
);
